/**
 * API URL Configuration
 * 
 * This file contains all API endpoint URLs used in the BVA application.
 * Centralizing URLs makes it easier to:
 * - Update endpoints in one place
 * - Switch between development, staging, and production environments
 * - Maintain consistency across the application
 */

// Determine if we're in production environment
const isProduction = process.env.NODE_ENV === 'production';

// Base API URL - use environment variable if available, otherwise use appropriate URL based on environment
export const API_BASE_URL = process.env.REACT_APP_API_URL || 
                    (isProduction ? 'https://bangalorevaluers.org/api/api' : 'http://127.0.0.1:5000/api');

// Auth endpoints
export const AUTH_URLS = {
  ADMIN_LOGIN: `${API_BASE_URL}/admin/login`,
  MEMBER_LOGIN: `${API_BASE_URL}/member/login`,
  MEMBER_REGISTER: `${API_BASE_URL}/member/register`,
  PASSWORD_RESET: `${API_BASE_URL}/auth/reset-password`,
  PASSWORD_RESET_REQUEST: `${API_BASE_URL}/auth/forgot-password`,
};

// Admin endpoints
export const ADMIN_URLS = {
  DASHBOARD: `${API_BASE_URL}/admin/dashboard`,
  PROFILE: `${API_BASE_URL}/admin/profile`,
  SETTINGS: `${API_BASE_URL}/admin/settings`,
  ADMINISTRATORS: `${API_BASE_URL}/admin/administrators`,
  
  // Member management
  MEMBERS: `${API_BASE_URL}/admin/members`,
  MEMBER_DETAIL: (id) => `${API_BASE_URL}/admin/members/${id}`,
  
  // Resource management
  RESOURCES: `${API_BASE_URL}/admin/resources`,
  RESOURCE_DETAIL: (id) => `${API_BASE_URL}/admin/resources/${id}`,
  
  // Program management
  PROGRAMS: `${API_BASE_URL}/admin/programs`,
  PROGRAM_DETAIL: (id) => `${API_BASE_URL}/admin/programs/${id}`,
  
  // Membership plans
  MEMBERSHIP_PLANS: `${API_BASE_URL}/admin/membership-plans`,
  MEMBERSHIP_PLAN_DETAIL: (id) => `${API_BASE_URL}/admin/membership-plans/${id}`,
  
  // Office bearers 
  OFFICE_BEARERS: `${API_BASE_URL}/admin/office-bearers`,
  OFFICE_BEARER_DETAIL: (id) => `${API_BASE_URL}/admin/office-bearers/${id}`,
  OFFICE_BEARER_REORDER: (id) => `${API_BASE_URL}/admin/office-bearers/${id}/reorder`,
  
  // Discounts
  DISCOUNTS: `${API_BASE_URL}/admin/discounts`,
  DISCOUNT_DETAIL: (id) => `${API_BASE_URL}/admin/discounts/${id}`,
  
  // Payments
  PAYMENTS: `${API_BASE_URL}/admin/payments`,
  MARK_AS_COMPLETE_PAYMENTS : (id) => `${API_BASE_URL}/admin/payments/mark-as-complete/${id}`,
  PAYMENT_DETAIL: (id) => `${API_BASE_URL}/admin/payments/${id}`,
  
  // Gallery
  GALLERY: `${API_BASE_URL}/admin/gallery`,
  GALLERY_ITEM: (id) => `${API_BASE_URL}/admin/gallery/${id}`,
  GALLERY_BULK_DELETE: `${API_BASE_URL}/admin/gallery/bulk-delete`,
  GALLERY_YOUTUBE_INFO: `${API_BASE_URL}/admin/gallery/youtube-info`,
  CHANGE_PASSWORD: `${API_BASE_URL}/admin/change-password`,
  ADMINS: `${API_BASE_URL}/admin/admins`,
};

// Member endpoints
export const MEMBER_URLS = {
  DASHBOARD: `${API_BASE_URL}/member/dashboard`,
  PROFILE: `${API_BASE_URL}/member/profile`,
  RESOURCES: `${API_BASE_URL}/member/resources`,
  PAYMENTS: `${API_BASE_URL}/member/payments`,
  PROGRAMS: `${API_BASE_URL}/member/programs`,
  PROGRAM_REGISTRATION: (programId) => `${API_BASE_URL}/member/programs/${programId}/register`,
  REGISTER: `${API_BASE_URL}/member/register`,
  VERIFY_REGISTRATION: `${API_BASE_URL}/member/verify-registration`,
  LOGIN: `${API_BASE_URL}/members/login`,
  PROFESSIONAL_LISTING: `${API_BASE_URL}/member/professional-listing`,
  VERIFY_PROFESSIONAL_LISTING_PAYMENT: `${API_BASE_URL}/member/verify-professional-listing-payment`,
  PROFESSIONAL_LISTING_STATUS: `${API_BASE_URL}/member/professional-listing/status`,
  CHANGE_PASSWORD: `${API_BASE_URL}/member/change-password`,
  // Discount endpoints
  GET_AVAILABLE_DISCOUNTS: `${API_BASE_URL}/member/available-discounts`,
  APPLY_DISCOUNT: `${API_BASE_URL}/member/apply-discount`,
  RENEW_MEMBERSHIP: `${API_BASE_URL}/member/renew-membership`,
};

// Public endpoints
export const PUBLIC_URLS = {
  MEMBERSHIP_PLANS: `${API_BASE_URL}/public/membership-plans`,
  PROGRAMS: `${API_BASE_URL}/public/programs`,
  PROGRAM_REGISTRATION: (programId) => `${API_BASE_URL}/public/programs/${programId}/register`,
  PROFESSIONALS: `${API_BASE_URL}/public/professionals`,
  PROFESSIONAL_LISTING_PRICE: `${API_BASE_URL}/public/professional-listing-price`,
  PROFESSIONAL_LISTING_ELIGIBILITY: `${API_BASE_URL}/public/professional-listing/eligibility`,
  GALLERY: `${API_BASE_URL}/public/gallery`,
  GALLERY_PHOTOS: `${API_BASE_URL}/public/gallery/photos`,
  GALLERY_VIDEOS: `${API_BASE_URL}/public/gallery/videos`,
  CONTACT: `${API_BASE_URL}/public/contact`,
  PROFILE: `${API_BASE_URL}/public/profile`,
  CHANGE_PASSWORD: `${API_BASE_URL}/public/change-password`,
  RENEW_MEMBERSHIP: `${API_BASE_URL}/public/renew-membership`,
  EVENTS: `${API_BASE_URL}/public/events`,
  NEWS: `${API_BASE_URL}/public/news`,
  OFFICE_BEARERS: `${API_BASE_URL}/public/office-bearers`,
  OFFICE_BEARER_DETAIL: (id) => `${API_BASE_URL}/public/office-bearers/${id}`,
  MEMBER_LOOKUP: `${API_BASE_URL}/member/lookup`,
  MEMBER_RENEWAL: `${API_BASE_URL}/member/renewal`,
  PAYMENT_VERIFICATION: `${API_BASE_URL}/public/payment/verify`,
  OFFICE_BEARERS_PAST: `${API_BASE_URL}/public/office-bearers/past`,
  OFFICE_BEARERS_PAST_BY_YEAR: `${API_BASE_URL}/public/office-bearers/past/by-year`,
  SETTINGS: `${API_BASE_URL}/public/settings`,
  // Resource endpoints
  RESOURCES: `${API_BASE_URL}/public/resources`,
  RESOURCE_DETAIL: (id) => `${API_BASE_URL}/public/resources/${id}`,
  RESOURCE_CATEGORIES: `${API_BASE_URL}/public/resources/categories`,
  
  // Discount endpoints
  GET_PUBLIC_DISCOUNTS: `${API_BASE_URL}/public/discounts?applicable_to=membership`,
  GET_MEMBERSHIP_PLANS: `${API_BASE_URL}/public/membership-plans`,
};

export default {
  BASE_URL: API_BASE_URL,
  AUTH: AUTH_URLS,
  ADMIN: ADMIN_URLS,
  MEMBER: MEMBER_URLS,
  PUBLIC: PUBLIC_URLS
 };

// Helper function to get proper resource URL
export const getResourceUrl = (url) => {
  if (!url) return '';
  
  // If URL starts with http/https, return as is (external URL)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If URL starts with /uploads, construct backend static URL
  if (url.startsWith('/uploads')) {
    return `${API_BASE_URL.replace('/api/api', '/api/static')}${url}`;
  }
  
  // Default fallback - assume it's a relative path to backend
  return `${API_BASE_URL.replace('/api', '')}${url.startsWith('/') ? url : '/' + url}`;
};